/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数 / renderRowActions は React
 * component ではなくテーブルセルの render 関数。 MasterDataTable は戻り値を
 * <TableCell> の children として表示するだけで独立した component subtree を
 * 持たないため、 lint 警告の前提 (= subtree 破棄) に該当しない
 * (AdminSingularDay/components/SingularDayTable.tsx と同判断)。
 */
import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  Box,
  HStack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { iconSize } from '~/constants/iconSize'
import { BilateralTradeDownloadModal } from '~/pages/AdminUploads/components/BilateralTradeDownloadModal'
import { CisContractDownloadModal } from '~/pages/AdminUploads/components/CisContractDownloadModal'
import { CustomerContractDownloadModal } from '~/pages/AdminUploads/components/CustomerContractDownloadModal'
import { DemandGroupDownloadModal } from '~/pages/AdminUploads/components/DemandGroupDownloadModal'
import { routePaths } from '~/routes/routePaths'

/**
 * ダウンロード列のスロット幅。 出力非対応の種別も同じ幅の空スロットを置いて
 * 列として揃えるため、 ボタンと空スロットで同一値を使う (= 先頭のアイコンと
 * 「ダウンロード」 ラベルが md ボタン (padding 16px) で折り返さない幅 = 16 + 20 + 8 + 84 + 16)。
 */
const DOWNLOAD_SLOT_WIDTH = '144px'

type UploadType = {
  id: string
  nameKey: string
  descKey: string
  href: string
  /**
   * 「ダウンロード」列の操作 (uploads/common.md §6)。 無し = 出力に対応しない。
   * modal = 全量 CSV の確認モーダルを開く。 link = モーダルを開かず業務画面へ
   * 遷移する (AC-DUC-028 ※: 48点データは業務画面で絞り込んでから取得する)。
   */
  download?: { kind: 'modal' } | { kind: 'link'; to: string }
}

/**
 * アップロード可能なデータ種別 (design `admin/uploads/index.html` dataTypes
 * のうち PR 1 スコープの 3 種別のみ)。
 *
 * [design差異] design の一覧は 件数 / 最新アップロードのステータス / 最終更新 /
 * 履歴リンク を持つが、実データ表示に必要な BFF RPC が未実装のため本 PR は
 * 種別 + 説明 + 導線のみの最小実装とする (ユーザー判断 2026-07-23。ステータス
 * 系列と履歴画面は後続スライス)。
 * 表示名・説明文は **spec `docs/specs/admin/data/uploads/index.md` の
 * 「データ種別の表示名・説明文」表が正本** (同 §「文言変更は本 spec の更新をもって
 * 行い、 実装・プロトタイプ側で独自の説明文を持たない」)。 i18n の値を変える時は
 * 先に spec を見ること。 以前は実装が独自の説明文を持っており、 48点データが
 * 「太陽光・風力発電・需要の30分実績」 という別物の説明になっていた (#4325)。
 * これに伴い、 説明文が design 由来だった頃の [design差異] 注記 (相対取引契約の
 * 「2ファイル」・CIS契約マスタの「料金プラン紐付け」を落としている旨) は、
 * 説明文の出所が design から spec に移ったため不要になり削除した。
 *
 * [2026-09-29 / #5793 追従完了] 行ラベルを spec 上の正式名称「供給地点マスタ」
 * (supply-point-master.md) へ改称した。以前はここに「画面表示名の改称は別タスクの
 * scope のためあえて改称しない (spec が正本の唯一の意図的な例外)」と書いていたが、
 * その受け皿タスクが起票されないまま、ダウンロード確認モーダル・ファイル名
 * (CisContractDownloadModal) だけが spec 準拠で「供給地点マスタ」を使う
 * **過渡的な不一致が同一画面内に残り続けていた**。例外を解消して正本に揃える。
 *
 * 改称したのは**画面に出る文言だけ**。route の key (`adminUploadsCisContract`)・
 * path・コンポーネント名 (`CisContract*`) は識別子なので触っていない
 * (リンクや import が壊れる範囲で、改称の意図とは別軸)。
 */
const UPLOAD_TYPES: UploadType[] = [
  {
    id: 'supply-48data',
    nameKey: 'page:adminUploads.types.supply48Data.name',
    descKey: 'page:adminUploads.types.supply48Data.desc',
    href: routePaths.adminUploadsSupply48Data.path,
    download: { kind: 'link', to: routePaths.supply48Data.path },
  },
  {
    id: 'bilateral-trade',
    nameKey: 'page:adminUploads.types.bilateralTrade.name',
    descKey: 'page:adminUploads.types.bilateralTrade.desc',
    href: routePaths.adminUploadsBilateralTrade.path,
    download: { kind: 'modal' },
  },
  {
    id: 'cis-contract',
    nameKey: 'page:adminUploads.types.cisContract.name',
    descKey: 'page:adminUploads.types.cisContract.desc',
    href: routePaths.adminUploadsCisContract.path,
    download: { kind: 'modal' },
  },
  {
    id: 'demand-group',
    nameKey: 'page:adminUploads.types.demandGroups.name',
    descKey: 'page:adminUploads.types.demandGroups.desc',
    href: routePaths.adminUploadsDemandGroups.path,
    download: { kind: 'modal' },
  },
  {
    id: 'customer-contract',
    nameKey: 'page:adminUploads.types.customerContract.name',
    descKey: 'page:adminUploads.types.customerContract.desc',
    href: routePaths.adminUploadsCustomerContract.path,
    download: { kind: 'modal' },
  },
]

/**
 * CSVアップロード一覧画面 (design `admin/uploads/index.html`)。
 *
 * システム管理 > CSVアップロード の独立画面 (design 2026-07-16 の独立カテゴリ
 * 化に追従)。データ種別を選んで各専用アップロードウィザード画面へ遷移する
 * ハブ画面 (spec `docs/specs/admin/data/uploads/index.md` の導線部分)。
 */
export const AdminUploadsPage = (): React.ReactElement => {
  const { t } = useTranslation()
  /** ダウンロード確認モーダルを開いている種別 id (null = 閉じている)。 */
  const [downloadTypeId, setDownloadTypeId] = useState<string | null>(null)

  const columns: Array<MasterDataTableColumn<UploadType>> = [
    {
      key: 'type',
      header: t('page:adminUploads.column.type'),
      cell: (row) => (
        <Box>
          <Text fontWeight="semibold">{t(row.nameKey)}</Text>
          <Text color="Text.midEmphasis" fontSize="sm">
            {t(row.descKey)}
          </Text>
        </Box>
      ),
    },
  ]

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminUploads.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        { label: t('common:route.adminUploads'), bold: true },
      ]}
    >
      <PageBody>
        <PageScrollArea>
          <HStack
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={3}
            marginBottom={4}
          >
            {/* ページ説明 (design admin/uploads/index.html `.page-description`) */}
            <PageIntro description={t('page:adminUploads.description')} />
            {/* CSVアップロード履歴画面への導線 (design admin/uploads/index.html
                `.ul-desc-row` の履歴ボタン、docs/specs/admin/data/uploads/history.md)。 */}
            <TertiaryButton asChild size="sm">
              <Link to={routePaths.adminUploadHistory.path}>
                {t('page:adminUploads.action.viewHistory')}
              </Link>
            </TertiaryButton>
          </HStack>
          <MasterDataTable
            actionColWidth="320px"
            columns={columns}
            getRowKey={(row) => row.id}
            renderRowActions={(row) => (
              <HStack justifyContent="flex-end" gap={2}>
                <TertiaryButton asChild size="md">
                  <Link to={row.href}>
                    {t('page:adminUploads.action.start')}
                  </Link>
                </TertiaryButton>
                {/*
                  ダウンロード列。 出力に対応しない種別でも同じ幅の空スロットを
                  置き、 行をまたいでボタンの左端が揃うようにする (= 列として
                  読める並びにする。 対応種別が増えたらここが埋まる)。
                */}
                <Box minWidth={DOWNLOAD_SLOT_WIDTH}>
                  {row.download?.kind === 'link' && (
                    <TertiaryButton width="100%" asChild size="md">
                      <Link to={row.download.to}>
                        <FaArrowDownToBracketRegular
                          boxSize={iconSize.labeledButton}
                        />
                        {t('page:adminUploads.action.download')}
                      </Link>
                    </TertiaryButton>
                  )}
                  {row.download?.kind === 'modal' && (
                    <TertiaryButton
                      width="100%"
                      onClick={() => setDownloadTypeId(row.id)}
                      size="md"
                    >
                      <FaArrowDownToBracketRegular
                        boxSize={iconSize.labeledButton}
                      />
                      {t('page:adminUploads.action.download')}
                    </TertiaryButton>
                  )}
                </Box>
              </HStack>
            )}
            rows={UPLOAD_TYPES}
          />

          <BilateralTradeDownloadModal
            isOpen={downloadTypeId === 'bilateral-trade'}
            onClose={() => setDownloadTypeId(null)}
          />
          <CisContractDownloadModal
            isOpen={downloadTypeId === 'cis-contract'}
            onClose={() => setDownloadTypeId(null)}
          />
          <DemandGroupDownloadModal
            isOpen={downloadTypeId === 'demand-group'}
            onClose={() => setDownloadTypeId(null)}
          />
          <CustomerContractDownloadModal
            isOpen={downloadTypeId === 'customer-contract'}
            onClose={() => setDownloadTypeId(null)}
          />
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
